import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import { Plus, Sparkles, Edit3, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip } from "recharts";

const CATEGORIES = [
  "Digital Subscription",
  "Food & Dining",
  "Handlooms & Fashions",
  "Handicrafts",
  "Yatras",
  "Toys",
  "Books & Games",
  "Health Foods & Snacks",
  "Stationeries",
  "Social & Emotional Learning",
];

export default function VendorDashboard() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [vendor, setVendor] = useState(null);
  const [fetching, setFetching] = useState(true);
  const [stats, setStats] = useState(null);
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [tab, setTab] = useState("overview");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    setFetching(true);
    try {
      const v = await api.get("/vendors/me");
      setVendor(v.data || null);
      if (!v.data || v.data.status !== "approved") return;
      const [s, p, o] = await Promise.all([
        api.get("/vendor/stats"),
        api.get("/products/mine"),
        api.get("/vendor/orders"),
      ]);
      setStats(s.data); setProducts(p.data); setOrders(o.data);
    } catch (err) {
      // 404 => no vendor profile yet
      setVendor(null);
    } finally {
      setFetching(false);
    }
  };

  useEffect(() => {
    if (loading) return;
    if (!user) { navigate("/auth"); return; }
    load();
    // eslint-disable-next-line
  }, [user, loading]);

  // New seller without profile → send them to the application form
  useEffect(() => {
    if (!fetching && !vendor) {
      navigate("/vendor/apply", { replace: true });
    }
  }, [fetching, vendor, navigate]);

  if (fetching) return <div data-testid="vendor-dashboard-loading" className="max-w-6xl mx-auto px-6 py-16 text-[#718096]">Loading…</div>;
  if (!vendor) return null;
  if (vendor.status !== "approved") {
    return (
      <div className="max-w-3xl mx-auto px-6 py-16">
        <div className="card-flat p-8 text-center">
          <h1 className="text-3xl">Your shop is under review</h1>
          <p className="mt-3 text-[#4A5568]">Status: <b>{vendor.status}</b>. We&apos;ll email you once approved.</p>
          <button onClick={() => navigate("/vendor/apply")} className="mt-6 btn-outline">Edit application</button>
        </div>
      </div>
    );
  }

  const chartData = [
    { name: "Products", value: stats?.products || 0 },
    { name: "Orders", value: stats?.orders || 0 },
    { name: "Revenue (₹k)", value: Math.round((stats?.revenue || 0) / 1000) },
  ];

  return (
    <div className="max-w-7xl mx-auto px-6 py-10">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <div>
          <div className="label-caps mb-2">Vendor Dashboard</div>
          <h1 className="text-4xl">{vendor.business_name}</h1>
          <div className="text-sm text-[#718096] mt-1">{vendor.location}</div>
        </div>
        <div className="flex gap-2">
          {["overview", "products", "orders", "gallery", "payout"].map((t) => (
            <button key={t} onClick={() => setTab(t)} data-testid={`vendor-tab-${t}`}
              className={`px-4 py-2 rounded-full text-sm font-semibold border ${tab === t ? "bg-[#13294B] text-white border-[#13294B]" : "bg-white border-[#E2DCD0] text-[#13294B]"}`}>
              {t.charAt(0).toUpperCase() + t.slice(1)}
            </button>
          ))}
        </div>
      </div>

      {tab === "overview" && stats && (
        <>
          <div className="grid md:grid-cols-4 gap-4 mb-8">
            <StatCard label="Products" value={stats.products} />
            <StatCard label="Orders" value={stats.orders} />
            <StatCard label="Revenue" value={`₹${stats.revenue.toLocaleString('en-IN')}`} />
            <StatCard label="Payout (95%)" value={`₹${stats.payout.toLocaleString('en-IN')}`} />
          </div>
          <div className="card-flat p-6">
            <div className="label-caps mb-4">Snapshot</div>
            <div style={{ width: "100%", height: 280 }}>
              <ResponsiveContainer>
                <BarChart data={chartData}>
                  <XAxis dataKey="name" stroke="#4A5568" fontSize={12} />
                  <YAxis stroke="#4A5568" fontSize={12} />
                  <Tooltip />
                  <Bar dataKey="value" fill="#E25A24" radius={[8, 8, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </>
      )}

      {tab === "products" && (
        <>
          <div className="mb-4 flex justify-end">
            <button onClick={() => { setEditing(null); setDialogOpen(true); }} data-testid="add-product-btn" className="btn-primary inline-flex items-center gap-2">
              <Plus className="w-4 h-4" /> New product
            </button>
          </div>
          <div className="card-flat overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-[#F2EFEB] text-left">
                <tr>
                  <th className="p-4 label-caps">Product</th>
                  <th className="p-4 label-caps">Category</th>
                  <th className="p-4 label-caps">Price</th>
                  <th className="p-4 label-caps">Stock</th>
                  <th className="p-4"></th>
                </tr>
              </thead>
              <tbody>
                {products.map((p) => (
                  <tr key={p.id} className="border-t border-[#E2DCD0]" data-testid={`product-row-${p.id}`}>
                    <td className="p-4">
                      <div className="flex items-center gap-3">
                        {p.images?.[0] && <img src={p.images[0]} alt="" className="w-10 h-10 rounded object-cover" />}
                        <span className="font-semibold">{p.title}</span>
                      </div>
                    </td>
                    <td className="p-4 text-[#4A5568]">{p.category}</td>
                    <td className="p-4 font-semibold">₹{p.price}</td>
                    <td className="p-4">{p.stock}</td>
                    <td className="p-4 text-right">
                      <button onClick={() => { setEditing(p); setDialogOpen(true); }} className="p-2 hover:text-[#E25A24]"><Edit3 className="w-4 h-4" /></button>
                      <button onClick={async () => { await api.delete(`/products/${p.id}`); toast.success("Deleted"); load(); }} className="p-2 hover:text-[#E25A24]"><Trash2 className="w-4 h-4" /></button>
                    </td>
                  </tr>
                ))}
                {products.length === 0 && <tr><td className="p-8 text-center text-[#718096]" colSpan={5}>No products yet.</td></tr>}
              </tbody>
            </table>
          </div>
          <ProductDialog open={dialogOpen} onOpenChange={setDialogOpen} editing={editing} onSaved={() => { load(); setDialogOpen(false); }} />
        </>
      )}

      {tab === "orders" && (
        <div className="space-y-3">
          {orders.map((o) => (
            <div key={o.id} className="card-flat p-4" data-testid={`vendor-order-${o.id}`}>
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <div className="font-semibold">Order #{o.id.slice(0, 8)}</div>
                  <div className="text-xs text-[#718096]">{o.buyer_name} · {new Date(o.created_at).toLocaleString('en-IN')}</div>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-xs font-bold uppercase px-2 py-1 rounded bg-[#F2EFEB]">{o.status}</span>
                  <select data-testid={`order-status-${o.id}`} onChange={async (e) => { await api.post(`/vendor/orders/${o.id}/status?status=${e.target.value}`); toast.success("Status updated"); load(); }} defaultValue="" className="px-2 py-1 text-xs border border-[#E2DCD0] rounded">
                    <option value="" disabled>Update…</option>
                    <option value="paid">Paid</option>
                    <option value="shipped">Shipped</option>
                    <option value="delivered">Delivered</option>
                    <option value="cancelled">Cancelled</option>
                  </select>
                </div>
              </div>
              <div className="mt-3 text-xs text-[#4A5568]">Ship to: {o.address} · {o.phone}</div>
            </div>
          ))}
          {orders.length === 0 && <div className="text-[#718096] text-sm">No orders yet.</div>}
        </div>
      )}

      {tab === "gallery" && (
        <GalleryTab vendor={vendor} onChange={load} />
      )}

      {tab === "payout" && stats && (
        <div className="grid md:grid-cols-2 gap-4">
          <StatCard label="Gross revenue" value={`₹${stats.revenue.toLocaleString('en-IN')}`} />
          <StatCard label="Platform commission (5%)" value={`₹${stats.commission.toLocaleString('en-IN')}`} />
          <div className="md:col-span-2 card-flat p-6">
            <div className="label-caps">Net payout</div>
            <div className="text-4xl font-bold mt-2 text-[#275F36]">₹{stats.payout.toLocaleString('en-IN')}</div>
            <p className="text-xs text-[#718096] mt-3">Payouts are settled weekly to your registered bank account.</p>
          </div>
        </div>
      )}
    </div>
  );
}

function StatCard({ label, value }) {
  return (
    <div className="card-flat p-6">
      <div className="label-caps">{label}</div>
      <div className="text-3xl font-bold mt-2 font-serif text-[#13294B]">{value}</div>
    </div>
  );
}

function GalleryTab({ vendor, onChange }) {
  const [uploading, setUploading] = useState(false);
  const [caption, setCaption] = useState("");
  const gallery = vendor.gallery || [];
  const backendUrl = process.env.REACT_APP_BACKEND_URL;

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 25 * 1024 * 1024) { toast.error("File must be under 25MB"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd);
      const url = `${backendUrl}${data.url}`;
      const type = file.type.startsWith("video/") ? "video" : "image";
      await api.post("/vendors/gallery", { url, type, caption: caption || null });
      toast.success("Added to your workshop gallery");
      setCaption("");
      onChange();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally { setUploading(false); e.target.value = ""; }
  };

  const remove = async (id) => {
    await api.delete(`/vendors/gallery/${id}`);
    toast.success("Removed");
    onChange();
  };

  return (
    <div>
      <div className="card-flat p-6 mb-6">
        <div className="label-caps mb-2">Add to your workshop story</div>
        <p className="text-sm text-[#4A5568] mb-4">Upload photos or short videos (up to 25MB) of your workspace, tools, and process — these appear on your maker page.</p>
        <div className="grid md:grid-cols-3 gap-3">
          <input
            data-testid="gallery-caption"
            value={caption}
            onChange={(e) => setCaption(e.target.value)}
            placeholder="Caption (optional)"
            className="md:col-span-2 px-3 py-2.5 border border-[#E2DCD0] rounded-lg text-sm focus:outline-none focus:border-[#E25A24]"
          />
          <label className={`btn-primary text-sm text-center cursor-pointer inline-flex items-center justify-center gap-2 ${uploading ? "opacity-50 pointer-events-none" : ""}`}>
            <Plus className="w-4 h-4" /> {uploading ? "Uploading…" : "Upload photo/video"}
            <input data-testid="gallery-upload" type="file" accept="image/*,video/*" onChange={onUpload} className="hidden" />
          </label>
        </div>
      </div>

      {gallery.length === 0 ? (
        <div className="text-center py-16 text-[#718096] text-sm">
          Your workshop gallery is empty. Add your first photo or video above.
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {gallery.map((g) => (
            <div key={g.id} className="relative group aspect-square rounded-xl overflow-hidden bg-[#F2EFEB]" data-testid={`gallery-tile-${g.id}`}>
              {g.type === "video"
                ? <video src={g.url} className="w-full h-full object-cover" muted playsInline />
                : <img src={g.url} alt={g.caption || ""} className="w-full h-full object-cover" />
              }
              {g.caption && <div className="absolute bottom-0 left-0 right-0 p-2 bg-gradient-to-t from-black/70 to-transparent text-white text-xs">{g.caption}</div>}
              <button
                onClick={() => remove(g.id)}
                data-testid={`gallery-delete-${g.id}`}
                className="absolute top-2 right-2 w-8 h-8 rounded-full bg-black/60 text-white grid place-items-center opacity-0 group-hover:opacity-100 transition-opacity"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ProductDialog({ open, onOpenChange, editing, onSaved }) {
  const [form, setForm] = useState({ title: "", description: "", price: 0, category: "Digital Subscription", region: "", stock: 10, images: [], tags: [] });
  const [genLoading, setGenLoading] = useState(false);
  const [keywords, setKeywords] = useState("");

  useEffect(() => {
    if (editing) setForm({ ...editing });
    else setForm({ title: "", description: "", price: 0, category: "Digital Subscription", region: "", stock: 10, images: [], tags: [] });
    // setForm is a stable state setter; editing/open are the only reactive inputs
  }, [editing, open]);

  const generate = async () => {
    if (!form.title) { toast.error("Enter a title first"); return; }
    setGenLoading(true);
    try {
      const { data } = await api.post("/products/generate-description", { title: form.title, category: form.category, keywords });
      setForm({ ...form, description: data.description });
      toast.success("Description generated");
    } catch { toast.error("AI failed"); }
    finally { setGenLoading(false); }
  };

  const uploadImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/upload", fd);
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      setForm({ ...form, images: [...form.images, `${backendUrl}${data.url}`] });
      toast.success("Image uploaded");
    } catch { toast.error("Upload failed"); }
  };

  const save = async () => {
    try {
      if (editing) await api.put(`/products/${editing.id}`, form);
      else await api.post("/products", form);
      toast.success("Saved");
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl bg-white max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl">{editing ? "Edit product" : "New product"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <input data-testid="product-title-input" placeholder="Title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="w-full px-3 py-2 border border-[#E2DCD0] rounded-lg" />
          <div className="grid grid-cols-2 gap-3">
            <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="px-3 py-2 border border-[#E2DCD0] rounded-lg">
              {CATEGORIES.map((c) => <option key={c}>{c}</option>)}
            </select>
            <input placeholder="Region (e.g. Kerala)" value={form.region} onChange={(e) => setForm({ ...form, region: e.target.value })} className="px-3 py-2 border border-[#E2DCD0] rounded-lg" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <input type="number" placeholder="Price ₹" value={form.price} onChange={(e) => setForm({ ...form, price: parseFloat(e.target.value) || 0 })} className="px-3 py-2 border border-[#E2DCD0] rounded-lg" />
            <input type="number" placeholder="Stock" value={form.stock} onChange={(e) => setForm({ ...form, stock: parseInt(e.target.value) || 0 })} className="px-3 py-2 border border-[#E2DCD0] rounded-lg" />
          </div>
          <div className="p-3 bg-[#F2EFEB] rounded-lg">
            <div className="label-caps mb-2">AI description</div>
            <input placeholder="Keywords (e.g. handmade, cotton, floral)" value={keywords} onChange={(e) => setKeywords(e.target.value)} className="w-full px-3 py-2 border border-[#E2DCD0] rounded-lg text-sm mb-2" />
            <button onClick={generate} disabled={genLoading} data-testid="generate-description-btn" className="btn-secondary !py-2 !px-4 text-sm inline-flex items-center gap-2">
              <Sparkles className="w-4 h-4" /> {genLoading ? "Generating…" : "Generate description"}
            </button>
          </div>
          <textarea data-testid="product-description-input" placeholder="Description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={5} className="w-full px-3 py-2 border border-[#E2DCD0] rounded-lg" />
          <div>
            <label className="label-caps">Product images</label>
            <div className="flex flex-wrap gap-2 mt-2">
              {form.images.map((img, i) => (
                <div key={`${img}-${i}`} className="w-20 h-20 rounded-lg overflow-hidden relative">
                  <img src={img} alt="" className="w-full h-full object-cover" />
                  <button onClick={() => setForm({ ...form, images: form.images.filter((_, j) => j !== i) })} className="absolute top-1 right-1 w-5 h-5 rounded-full bg-black/60 text-white text-xs">×</button>
                </div>
              ))}
              <label className="w-20 h-20 rounded-lg border-2 border-dashed border-[#E2DCD0] grid place-items-center cursor-pointer hover:border-[#E25A24]">
                <Plus className="w-5 h-5 text-[#718096]" />
                <input data-testid="product-image-upload" type="file" accept="image/*" onChange={uploadImage} className="hidden" />
              </label>
            </div>
          </div>
          <button onClick={save} data-testid="save-product-btn" className="w-full btn-primary">Save product</button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
